/**
 * Mesa Vision C T8 + SDD 006 T4 — seeds (RF-15c, RF-16..19, RF-1..2). Pure builders, no storage.
 * - propiedad: the interview drives the study — program / area → rooms laid out as a real plan
 * - producto / mecanismo / pinout: small fixed packages that are usable (parts + params + derived views)
 *   plus real catalog CAD specs (`part.cad`) for `producto` and `mecanismo` (SDD 006).
 * Demo briefs answer the interview so every template exercises the same path.
 */

import type {
  DomainTemplateId,
  StudyAnnexRender,
  StudyPart,
  StudyPartCad,
  StudyPartParam,
} from "@/lib/studyPackageTypes";

const r2 = (n: number) => Math.round(n * 20) / 20; // 0.05 grid

function knobs(w: number, d: number, h: number, unit = "m"): StudyPartParam[] {
  return [
    { id: "w", label: "Ancho", unit, value: r2(w) },
    { id: "d", label: "Fondo", unit, value: r2(d) },
    { id: "h", label: "Alto", unit, value: r2(h) },
  ];
}

// ─── propiedad: brief → rooms → plan ─────────────────────────────────────────

type Zone = "exterior" | "social" | "privada";
type Room = { id: string; name: string; role: string; area: number; h: number; zone: Zone; interior: boolean };

const SMALL_ROOM_M2 = 7;

const NUM_WORDS: Record<string, number> = { un: 1, una: 1, uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5 };

function norm(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function countOf(text: string, nouns: string, fallback: number): number {
  const m = text.match(new RegExp(`\\b(\\d+(?:[.,]5)?|${Object.keys(NUM_WORDS).join("|")})\\s*(?:${nouns})`));
  if (!m) return new RegExp(nouns).test(text) ? Math.max(fallback, 1) : fallback;
  return NUM_WORDS[m[1]] ?? parseFloat(m[1].replace(",", "."));
}

/** "1,400 pies²", "130 m2", "1400" (bare > 300 → ft²) → m². Null when absent. */
export function parseAreaM2(raw: string | undefined): number | null {
  if (!raw) return null;
  const t = norm(raw);
  const m = t.match(/(\d{1,3}(?:[.,]\d{3})+|\d+(?:[.,]\d+)?)\s*(m2|m²|metros?|mts?|pies?|ft|p2|sq)?/);
  if (!m) return null;
  const num = parseFloat(/[.,]\d{3}\b/.test(m[1]) ? m[1].replace(/[.,]/g, "") : m[1].replace(",", "."));
  if (!Number.isFinite(num) || num <= 0) return null;
  const unit = m[2] ?? (num > 300 ? "ft" : "m2");
  return /^(pie|ft|p2|sq)/.test(unit) ? num * 0.0929 : num;
}

/** Program text → room list with typical PR residential sizes (m²). */
export function roomsFromProgram(program: string): Room[] {
  const t = norm(program);
  const rooms: Room[] = [];
  const add = (r: Omit<Room, "interior">) => rooms.push({ ...r, interior: r.zone !== "exterior" });

  const hasComedor = /comedor/.test(t);
  add({ id: "sala", name: hasComedor ? "Sala-comedor" : "Sala", role: "espacio", area: hasComedor ? 30 : 22, h: 3, zone: "social" });
  add({ id: "cocina", name: "Cocina", role: "espacio", area: 13, h: 3, zone: "social" });

  const dorms = Math.round(countOf(t, "dorm\\w*|cuartos?|habitacion\\w*|recamaras?|bedrooms?", 2));
  for (let i = 1; i <= Math.min(Math.max(dorms, 1), 6); i++) {
    add({
      id: `dorm-${i}`,
      name: i === 1 ? "Dormitorio principal" : `Dormitorio ${i}`,
      role: "espacio",
      area: i === 1 ? 16 : 11,
      h: 3,
      zone: "privada",
    });
  }

  const banos = countOf(t, "banos?|bathrooms?", 1);
  for (let i = 1; i <= Math.min(Math.floor(banos) || 1, 4); i++) {
    add({ id: `bano-${i}`, name: `Baño ${i}`, role: "espacio", area: 5.5, h: 3, zone: "privada" });
  }
  if (banos % 1 !== 0 || /medio bano/.test(t)) {
    add({ id: "medio-bano", name: "Medio baño", role: "espacio", area: 3, h: 3, zone: "social" });
  }

  if (/oficina|despacho|office/.test(t)) add({ id: "oficina", name: "Oficina", role: "espacio", area: 10, h: 3, zone: "privada" });
  if (/lavander|laundry/.test(t)) add({ id: "lavanderia", name: "Lavandería", role: "espacio", area: 5, h: 3, zone: "social" });
  if (/taller|workshop/.test(t)) add({ id: "taller", name: "Taller", role: "espacio", area: 18, h: 3.4, zone: "social" });
  if (/terraza|balcon|patio|porch/.test(t)) {
    add({ id: "terraza", name: "Terraza techada", role: "espacio", area: 18, h: 2.7, zone: "exterior" });
  }
  if (/marquesina|garaje|garage|carport|estacionamiento/.test(t)) {
    add({ id: "marquesina", name: "Marquesina", role: "estructura", area: 22, h: 2.8, zone: "exterior" });
  }
  if (/cisterna/.test(t)) add({ id: "cisterna", name: "Cisterna", role: "estructura", area: 3, h: 2, zone: "exterior" });
  return rooms;
}

/**
 * Strip-packs rooms into zone bands (exterior front, social, privada back) of one plan width,
 * so rooms share walls and the plan reads as a house. Interior area is scaled toward the brief.
 */
export function layoutRooms(rooms: Room[], targetInteriorM2: number | null): StudyPart[] {
  const interior = rooms.filter((r) => r.interior).reduce((s, r) => s + r.area, 0);
  const k = targetInteriorM2 ? Math.min(Math.max(targetInteriorM2 / interior, 0.6), 1.8) : 1;
  const sized = rooms.map((r) => ({ ...r, area: r.interior ? r.area * k : r.area }));
  const planW = Math.sqrt(sized.filter((r) => r.interior).reduce((s, r) => s + r.area, 0) * 1.4);

  const parts: StudyPart[] = [];
  let z = 0;
  for (const zone of ["exterior", "social", "privada"] as Zone[]) {
    const band = sized.filter((r) => r.zone === zone);
    if (band.length === 0) continue;
    const total = band.reduce((s, r) => s + r.area, 0);
    // Exterior band keeps a usable depth even when small (a car needs ~5.5 m).
    // Positions accumulate the rounded sizes so 0.05 m rounding never makes neighbours overlap.
    const depth = r2(Math.max(total / planW, zone === "exterior" ? 3 : 2.8));
    // Small rooms (baños, lavandería…) stack in one column so they keep room-like proportions
    // instead of becoming 1.3 m × 5 m corridors.
    const small = band.filter((r) => r.area <= SMALL_ROOM_M2);
    const large = band.filter((r) => r.area > SMALL_ROOM_M2);
    let x = 0;
    for (const room of large) {
      const w = r2(room.area / depth);
      parts.push({
        id: room.id,
        name: room.name,
        role: room.role,
        params: knobs(w, depth, room.h),
        origin: { x: r2(x), z: r2(z) },
      });
      x += w;
    }
    if (small.length > 0) {
      // Min 2.2 m wide: a lone lavandería gets a real room; the rest of the column stays open (pasillo).
      const colW = r2(Math.max(small.reduce((s, r) => s + r.area, 0) / depth, 2.2));
      let zz = z;
      for (const room of small) {
        const d = r2(room.area / colW);
        parts.push({
          id: room.id,
          name: room.name,
          role: room.role,
          params: knobs(colW, d, room.h),
          origin: { x: r2(x), z: r2(zz) },
        });
        zz += d;
      }
    }
    z += depth;
  }
  return parts;
}

export function propertyPartsFromBrief(answers: Record<string, string>): StudyPart[] {
  const program = answers.prop_program?.trim() || "sala, cocina, 2 dormitorios, 1 baño";
  return layoutRooms(roomsFromProgram(program), parseAreaM2(answers.prop_area));
}

// ─── fixed seeds (mm) for non-property domains ───────────────────────────────

type SeedRow = [id: string, name: string, role: string, w: number, d: number, h: number, x: number, z: number];

function fixedParts(rows: SeedRow[], cadByPart?: Record<string, StudyPartCad>): StudyPart[] {
  return rows.map(([id, name, role, w, d, h, x, z]) => ({
    id,
    name,
    role,
    params: knobs(w, d, h, "mm"),
    origin: { x, z },
    ...(cadByPart?.[id] ? { cad: structuredClone(cadByPart[id]) } : {}),
  }));
}

/** Exploded along x so ortho / despiece read part by part. */
const PRODUCTO: SeedRow[] = [
  ["base", "Base carcasa", "pieza", 120, 80, 20, 0, 0],
  ["junta", "Junta IP65", "cara", 116, 76, 3, 135, 2],
  ["pcb", "PCB ESP32 + BME280", "pieza", 70, 50, 14, 266, 15],
  ["tapa", "Tapa", "pieza", 120, 80, 25, 351, 0],
  ["prensaestopas", "Prensaestopas PG7", "fijacion", 15, 15, 22, 486, 32],
];

/** SDD 006 · RF-2: real catalog CAD definitions for `producto` (Caja sensor exterior IP65). */
export const PRODUCTO_CAD: Record<string, StudyPartCad> = {
  base: {
    type: "carcasa",
    params: {
      width: 120,
      depth: 80,
      height: 28,
      wall: 3,
      floor: 3,
      corner_radius: 4,
      bosses: 4,
      boss_diameter: 7,
      boss_hole: 2.9,
      boss_height: 8,
      boss_inset: 6,
    },
    ops: [{ op: "wall_hole", params: { face: "front", diameter: 12.5, u: 0, v: 14 } }],
    material: "asa",
    process: "impresion_3d",
    criticalDims: { width: 120, depth: 80, height: 28, wall: 3 },
  },
  junta: {
    type: "junta",
    params: { width: 116, depth: 76, band: 3, thickness: 2, corner_radius: 3 },
    material: "tpu",
    process: "impresion_3d",
    criticalDims: { width: 116, depth: 76, thickness: 2 },
  },
  pcb: {
    type: "placa",
    params: { width: 70, depth: 50, thickness: 2, corner_radius: 2 },
    ops: [
      { op: "hole", params: { diameter: 3.2, x: -30, y: -20 } },
      { op: "hole", params: { diameter: 3.2, x: 30, y: -20 } },
      { op: "hole", params: { diameter: 3.2, x: -30, y: 20 } },
      { op: "hole", params: { diameter: 3.2, x: 30, y: 20 } },
    ],
    material: "acrilico",
    process: "corte_laser",
    criticalDims: { width: 70, depth: 50, thickness: 2 },
  },
  tapa: {
    type: "tapa",
    params: {
      width: 120,
      depth: 80,
      thickness: 4,
      corner_radius: 4,
      lip_height: 3,
      lip_inset: 3.2,
      lip_wall: 2,
      screws: 4,
      screw_hole: 3.4,
      screw_inset: 6,
    },
    ops: [{ op: "hole", params: { diameter: 6, x: 25, y: 0 } }],
    material: "asa",
    process: "impresion_3d",
    criticalDims: { width: 120, depth: 80, thickness: 4 },
  },
  prensaestopas: {
    type: "fijacion",
    params: { size: "M3", length: 12 },
    material: "acero_inox_316",
    process: "compra",
    criticalDims: { length: 12 },
  },
};

const MECANISMO: SeedRow[] = [
  ["base-motor", "Base motor", "eslabon", 200, 150, 12, 0, 0],
  ["nema23", "NEMA 23", "actuador", 57, 57, 76, 40, 165],
  ["pinon", "Piñón M2", "union", 40, 40, 20, 120, 180],
  ["cremallera", "Cremallera 1 m", "eslabon", 1000, 20, 20, 0, 240],
  ["carro", "Carro / rueda guía", "union", 80, 60, 60, 220, 0],
];

/** SDD 006 · RF-2: real catalog CAD definitions for `mecanismo` (Abridor de portón). */
export const MECANISMO_CAD: Record<string, StudyPartCad> = {
  "base-motor": {
    type: "placa",
    params: { width: 200, depth: 150, thickness: 10, corner_radius: 6 },
    ops: [
      { op: "hole", params: { diameter: 38.2, x: 0, y: 0 } },
      { op: "slot", params: { length: 24, width: 6.5, x: -75, y: -50, angle: 0 } },
      { op: "slot", params: { length: 24, width: 6.5, x: 75, y: -50, angle: 0 } },
      { op: "slot", params: { length: 24, width: 6.5, x: -75, y: 50, angle: 0 } },
      { op: "slot", params: { length: 24, width: 6.5, x: 75, y: 50, angle: 0 } },
    ],
    material: "aluminio_6061",
    process: "corte_laser",
    criticalDims: { width: 200, depth: 150, thickness: 10 },
  },
  nema23: {
    type: "carcasa",
    params: {
      width: 57,
      depth: 57,
      height: 76,
      wall: 4,
      floor: 5,
      corner_radius: 4,
      bosses: 4,
      boss_diameter: 8,
      boss_hole: 5,
      boss_height: 8,
      boss_inset: 5,
    },
    material: "aluminio_6061",
    process: "cnc_3_ejes",
    criticalDims: { width: 57, depth: 57, height: 76 },
  },
  pinon: {
    type: "eje",
    params: { diameter: 40, length: 24, chamfer: 1 },
    ops: [{ op: "flat", params: { depth: 1.5, length: 16, start: 4 } }],
    material: "acero_inox_304",
    process: "torno",
    criticalDims: { diameter: 40, length: 24 },
  },
  cremallera: {
    type: "placa",
    params: { width: 320, depth: 20, thickness: 20, corner_radius: 1 },
    ops: [
      { op: "hole", params: { diameter: 6.5, x: -120, y: 0 } },
      { op: "hole", params: { diameter: 6.5, x: 0, y: 0 } },
      { op: "hole", params: { diameter: 6.5, x: 120, y: 0 } },
    ],
    material: "acero_inox_304",
    process: "cnc_3_ejes",
    criticalDims: { width: 320, depth: 20, thickness: 20 },
  },
  carro: {
    type: "placa",
    params: { width: 80, depth: 60, thickness: 12, corner_radius: 4 },
    ops: [
      { op: "pocket", params: { width: 40, depth: 24, cut_depth: 4, x: 0, y: 0 } },
      { op: "hole", params: { diameter: 8.5, x: -25, y: 0 } },
      { op: "hole", params: { diameter: 8.5, x: 25, y: 0 } },
    ],
    material: "aluminio_6061",
    process: "cnc_3_ejes",
    criticalDims: { width: 80, depth: 60, thickness: 12 },
  },
};

/** Pins / nets as discrete parts with stable ids (T9 bridges the pinout diagram to these). */
const PINOUT: SeedRow[] = [
  ["esp32", "ESP32 DevKit", "placa", 55, 28, 10, 0, 0],
  ["gpio21", "GPIO21 · SDA", "pin", 5, 5, 9, 62, 0],
  ["gpio22", "GPIO22 · SCL", "pin", 5, 5, 9, 70, 0],
  ["gpio26", "GPIO26 · Relé", "pin", 5, 5, 9, 78, 0],
  ["net-3v3", "3V3", "net", 5, 5, 6, 62, 10],
  ["net-gnd", "GND", "net", 5, 5, 6, 70, 10],
  ["net-5v", "5V", "net", 5, 5, 6, 78, 10],
  ["bme280", "BME280 (I2C)", "modulo", 15, 12, 3, 95, 0],
  ["rele", "Relé 5V", "modulo", 50, 26, 19, 0, 40],
  ["buck", "Buck 12→5V", "modulo", 43, 21, 14, 60, 40],
  ["cable-i2c", "Cable I2C", "cable", 30, 3, 3, 62, 25],
];

/** Wiring: pin / net → cable or module (T9 diagrama + tabla de pines read these). */
const PINOUT_LINKS: Record<string, string[]> = {
  gpio21: ["cable-i2c"],
  gpio22: ["cable-i2c"],
  "cable-i2c": ["bme280"],
  gpio26: ["rele"],
  "net-3v3": ["bme280"],
  "net-gnd": ["bme280", "rele", "buck"],
  "net-5v": ["rele", "buck"],
};

// ─── demo briefs ─────────────────────────────────────────────────────────────

export const DEMO_BRIEFS: Record<DomainTemplateId, Record<string, string>> = {
  propiedad: {
    prop_program: "Sala-comedor, cocina, 2 dormitorios, 2 baños, lavandería, taller y terraza techada; marquesina para 1 carro",
    prop_levels: "1",
    prop_area: "1,400 pies²",
    prop_resilience: "Ambos: huracán Cat 5 y salitre (costa)",
    prop_site: "Concepto sin terreno aún",
  },
  producto: {
    prod_function: "Caja exterior para sensor ESP32 + BME280 en la terraza",
    prod_envelope: "120 × 80 × 45 mm",
    prod_material: "ASA (resistente UV)",
    prod_ip: "IP65 · salitre",
  },
  mecanismo: {
    mech_dof: "1 DoF lineal · 3 m de recorrido (portón corredizo)",
    mech_load: "Portón de 120 kg",
    mech_drive: "NEMA 23 + piñón-cremallera",
    mech_env: "Costa, polvo",
  },
  pinout: {
    pin_mcu: "ESP32 DevKit v1",
    pin_peripherals: "BME280 por I2C (GPIO21/22) + relé 5V en GPIO26",
    pin_power: "Buck 12V → 5V",
  },
};

export const DEMO_TITLES: Record<DomainTemplateId, string> = {
  propiedad: "Casa de sueños",
  producto: "Caja sensor exterior",
  mecanismo: "Abridor de portón",
  pinout: "ESP32 + BME280 + relé",
};

/** Concept renders stay annexes (RF-22): never the master model. */
export function demoAnnexes(templateId: DomainTemplateId, ts: string): StudyAnnexRender[] {
  if (templateId !== "propiedad") return [];
  return [
    { id: "demo-render", url: "/demo-casa-suenos.jpg", prompt: "Concepto casa de sueños", createdAt: ts },
    { id: "demo-planta", url: "/demo-casa-suenos-planta.jpg", prompt: "Planta conceptual", createdAt: ts },
  ];
}

/** Parts for a template from its (possibly demo) brief. */
export function seedPartsFor(templateId: DomainTemplateId, answers: Record<string, string>): StudyPart[] {
  switch (templateId) {
    case "propiedad":
      return propertyPartsFromBrief(answers);
    case "producto":
      return fixedParts(PRODUCTO, PRODUCTO_CAD);
    case "mecanismo":
      return fixedParts(MECANISMO, MECANISMO_CAD);
    case "pinout":
      return fixedParts(PINOUT).map((p) => (PINOUT_LINKS[p.id] ? { ...p, connects: PINOUT_LINKS[p.id] } : p));
  }
}
